/**
 * LabProfilePage – Aggregated profile overview across all 3 pillars
 *
 * Shows: Lifestyle summary (Pillar 1), Rate analysis summary (Pillar 2),
 * Newbuild profile summary (Pillar 3) – each styled in pillar colors.
 * Data is always freshly loaded from the APIs on every visit.
 */

import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest, changePassword, validatePassword } from '../services/authService';

const API_BASE = import.meta.env.VITE_API_URL || '';

interface HeroImageSettings {
  enabled: boolean;
  filename: string | null;
  opacity: number;
  blend_mode: string;
  blur: number;
}

interface RateSummary {
  total: number;
  below: number;
  fair: number;
  above: number;
  unknown: number;
}

export default function LabProfilePage() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const passwordRef = useRef<HTMLDivElement>(null);

  /* ── state ── */
  const [loading, setLoading] = useState(true);
  const [lifestyleSummary, setLifestyleSummary] = useState<string | null>(null);
  const [lifestyleUpdated, setLifestyleUpdated] = useState<string | null>(null);
  const [rateSummary, setRateSummary] = useState<RateSummary>({ total: 0, below: 0, fair: 0, above: 0, unknown: 0 });
  const [newbuildSummary, setNewbuildSummary] = useState<string | null>(null);
  const [newbuildProfile, setNewbuildProfile] = useState<any>(null);
  const [newbuildUpdated, setNewbuildUpdated] = useState<string | null>(null);
  const [heroImage, setHeroImage] = useState<HeroImageSettings | null>(null);

  /* ── password change state ── */
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');

  /* scroll to #password anchor when arriving from dropdown */
  useEffect(() => {
    if (location.hash === '#password' && passwordRef.current) {
      setTimeout(() => passwordRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 400);
    }
  }, [location.hash, loading]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/#lab-auth');
      return;
    }
    loadAllData();
    // Load background image
    fetch(`${API_BASE}/api/admin/lab-profile-image-settings`)
      .then((r) => r.json())
      .then((data) => {
        if (data.success && data.data?.enabled && data.data?.filename) {
          setHeroImage(data.data);
        }
      })
      .catch(() => {});
  }, [isAuthenticated]);

  async function loadAllData() {
    setLoading(true);
    try {
      const [lifestyleRes, valuationsRes, newbuildRes] = await Promise.allSettled([
        apiRequest('/api/lifestyle-profile'),
        apiRequest('/api/valuations'),
        apiRequest('/api/newbuild-profile'),
      ]);

      // Pillar 1: Lifestyle
      if (lifestyleRes.status === 'fulfilled' && lifestyleRes.value?.success) {
        setLifestyleSummary(lifestyleRes.value.data?.summary || null);
        setLifestyleUpdated(lifestyleRes.value.data?.updatedAt || null);
      }

      // Pillar 2: Rate valuations – count & categorize
      if (valuationsRes.status === 'fulfilled' && valuationsRes.value?.success) {
        const allVals = valuationsRes.value.data?.valuations || [];
        let below = 0, fair = 0, above = 0, unknown = 0;
        for (const v of allVals) {
          const pe = v.priceEstimate || {};
          if (pe.assessment === 'below') below++;
          else if (pe.assessment === 'fair') fair++;
          else if (pe.assessment === 'above') above++;
          else unknown++;
        }
        setRateSummary({ total: allVals.length, below, fair, above, unknown });
      }

      // Pillar 3: Newbuild
      if (newbuildRes.status === 'fulfilled' && newbuildRes.value?.success) {
        const data = newbuildRes.value.data;
        setNewbuildSummary(data?.summary || null);
        setNewbuildProfile(data?.profile || null);
        setNewbuildUpdated(data?.updatedAt || null);
      }
    } catch (err) {
      console.error('[LabProfile] Load error:', err);
    } finally {
      setLoading(false);
    }
  }

  /** Build a readable text from the newbuild profile JSON if no summary exists */
  function buildNewbuildFallback(profile: any): string {
    if (!profile) return '';
    const parts: string[] = [];
    if (profile.project_type) parts.push(`Projekttyp: ${profile.project_type}`);
    if (profile.building_type) parts.push(`Gebäudetyp: ${profile.building_type}`);
    if (profile.num_units) parts.push(`Einheiten: ${profile.num_units}`);
    if (profile.living_area_m2) parts.push(`Wohnfläche: ${profile.living_area_m2} m²`);
    if (profile.num_floors) parts.push(`Stockwerke: ${profile.num_floors}`);
    if (profile.plot_address) parts.push(`Grundstück: ${profile.plot_address}`);
    if (profile.plot_municipality) parts.push(`Gemeinde: ${profile.plot_municipality}`);
    if (profile.budget_chf) parts.push(`Budget: CHF ${Number(profile.budget_chf).toLocaleString('de-CH')}`);
    if (profile.wishes?.length) parts.push(`Wünsche: ${profile.wishes.join(', ')}`);
    return parts.join(' · ');
  }

  async function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    setPwError('');
    setPwSuccess('');
    if (pwNew !== pwConfirm) {
      setPwError('Die Passwörter stimmen nicht überein.');
      return;
    }
    const pwErr = validatePassword(pwNew);
    if (pwErr) {
      setPwError(pwErr);
      return;
    }
    try {
      setPwLoading(true);
      await changePassword(pwCurrent, pwNew);
      setPwSuccess('Passwort erfolgreich geändert!');
      setPwCurrent('');
      setPwNew('');
      setPwConfirm('');
    } catch (err: any) {
      setPwError(err.message || 'Fehler beim Ändern des Passworts');
    } finally {
      setPwLoading(false);
    }
  }

  const userInitial = user?.name?.charAt(0)?.toUpperCase() || '?';
  const newbuildText = newbuildSummary || buildNewbuildFallback(newbuildProfile);

  return (
    <div className="lab-profile-page">
      {/* ── Background ── */}
      <div className="lab-orb lab-orb--1" />
      <div className="lab-orb lab-orb--2" />

      {heroImage && heroImage.enabled && heroImage.filename && (
        <div
          className="lab-bg-image"
          style={{
            backgroundImage: `url(/uploads/${heroImage.filename})`,
            opacity: heroImage.opacity / 100,
            mixBlendMode: heroImage.blend_mode as any,
            filter: heroImage.blur > 0 ? `blur(${heroImage.blur}px)` : undefined,
          }}
        />
      )}

      {/* ── Header ── */}
      <div className="lab-profile-header">
        <div className="lab-profile-avatar">
          <span>{userInitial}</span>
        </div>
        <h1 className="lab-profile-title">
          Mein SurrealEstate<span className="lab-profile-title-accent">Lab</span>-Profil
        </h1>
        <p className="lab-profile-subtitle">
          Übersicht über alle gespeicherten Informationen aus Ihren SurrealEstate-Aktivitäten.
        </p>
      </div>

      {loading ? (
        <div className="lab-profile-loading">
          <div className="spinner" />
          <p>Profildaten werden geladen...</p>
        </div>
      ) : (
        <div className="lab-profile-sections">
          {/* ═══ Pillar 1: Lifestyle ═══ */}
          <div className="lab-profile-section lab-profile-section--search">
            <div className="lab-profile-section-header">
              <div className="lab-profile-section-dot" style={{ background: '#6366f1' }} />
              <h2>
                SurrealEstate<span style={{ color: '#6366f1' }}>Search</span>
              </h2>
            </div>
            <div className="lab-profile-section-body">
              <h3 className="lab-profile-section-subtitle">Lebensstil-Profil</h3>
              {lifestyleSummary ? (
                <>
                  <p className="lab-profile-text">{lifestyleSummary}</p>
                  {lifestyleUpdated && (
                    <span className="lab-profile-date">
                      Aktualisiert: {new Date(lifestyleUpdated).toLocaleDateString('de-CH')}
                    </span>
                  )}
                </>
              ) : (
                <p className="lab-profile-empty">
                  Noch kein Lebensstil-Profil erstellt.
                </p>
              )}
              <Link to="/search" className="lab-profile-section-link" style={{ '--section-color': '#6366f1' } as React.CSSProperties}>
                Zur Suche
              </Link>
            </div>
          </div>

          {/* ═══ Pillar 2: Rate ═══ */}
          <div className="lab-profile-section lab-profile-section--rate">
            <div className="lab-profile-section-header">
              <div className="lab-profile-section-dot" style={{ background: '#00CED1' }} />
              <h2>
                SurrealEstate<span style={{ color: '#00CED1' }}>Rate</span>
              </h2>
            </div>
            <div className="lab-profile-section-body">
              <h3 className="lab-profile-section-subtitle">Bewertungen</h3>
              {rateSummary.total > 0 ? (
                <div className="lab-profile-rate-stats">
                  <div className="lab-profile-rate-total">
                    <span className="lab-profile-rate-count">{rateSummary.total}</span>
                    <span className="lab-profile-rate-label">{rateSummary.total === 1 ? 'Analyse' : 'Analysen'}</span>
                  </div>
                  <div className="lab-profile-rate-breakdown">
                    {rateSummary.below > 0 && (
                      <div className="lab-profile-rate-tag lab-profile-rate-tag--below">
                        <span className="lab-profile-rate-tag-dot" />
                        {rateSummary.below}x Unter Marktwert
                      </div>
                    )}
                    {rateSummary.fair > 0 && (
                      <div className="lab-profile-rate-tag lab-profile-rate-tag--fair">
                        <span className="lab-profile-rate-tag-dot" />
                        {rateSummary.fair}x Im Marktwert
                      </div>
                    )}
                    {rateSummary.above > 0 && (
                      <div className="lab-profile-rate-tag lab-profile-rate-tag--above">
                        <span className="lab-profile-rate-tag-dot" />
                        {rateSummary.above}x Über Marktwert
                      </div>
                    )}
                    {rateSummary.unknown > 0 && (
                      <div className="lab-profile-rate-tag lab-profile-rate-tag--unknown">
                        <span className="lab-profile-rate-tag-dot" />
                        {rateSummary.unknown}x Nicht bestimmbar
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <p className="lab-profile-empty">
                  Noch keine Bewertungen vorhanden.
                </p>
              )}
              <Link to="/rate" className="lab-profile-section-link" style={{ '--section-color': '#00CED1' } as React.CSSProperties}>
                Zur Bewertung
              </Link>
            </div>
          </div>

          {/* ═══ Pillar 3: Newbuild ═══ */}
          <div className="lab-profile-section lab-profile-section--newbuild">
            <div className="lab-profile-section-header">
              <div className="lab-profile-section-dot" style={{ background: '#C75B4A' }} />
              <h2>
                SurrealEstate<span style={{ color: '#C75B4A' }}>Newbuild</span>
              </h2>
            </div>
            <div className="lab-profile-section-body">
              <h3 className="lab-profile-section-subtitle">Neubauprofil</h3>
              {newbuildText ? (
                <>
                  <p className="lab-profile-text">{newbuildText}</p>
                  {newbuildUpdated && (
                    <span className="lab-profile-date">
                      Aktualisiert: {new Date(newbuildUpdated).toLocaleDateString('de-CH')}
                    </span>
                  )}
                </>
              ) : (
                <p className="lab-profile-empty">
                  Noch kein Neubauprofil erstellt.
                </p>
              )}
              <Link to="/newbuild" className="lab-profile-section-link" style={{ '--section-color': '#C75B4A' } as React.CSSProperties}>
                Zum Neubau
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Password Change Section ═══ */}
      <div className="lab-profile-password" id="password" ref={passwordRef}>
        <div className="lab-profile-password-card">
          <div className="lab-profile-password-header">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            <h3>Passwort ändern</h3>
          </div>
          <p className="lab-profile-password-desc">
            Ändern Sie hier das Passwort für Ihren gesamten SurrealEstate-Account.
          </p>

          {pwError && <div className="lab-profile-pw-msg lab-profile-pw-msg--error">{pwError}</div>}
          {pwSuccess && <div className="lab-profile-pw-msg lab-profile-pw-msg--success">{pwSuccess}</div>}

          <form onSubmit={handlePasswordSubmit} className="lab-profile-pw-form">
            <div className="lab-profile-pw-field">
              <label htmlFor="lab-pw-current">Aktuelles Passwort</label>
              <input
                type="password"
                id="lab-pw-current"
                value={pwCurrent}
                onChange={e => setPwCurrent(e.target.value)}
                required
                placeholder="Ihr aktuelles Passwort"
              />
            </div>
            <div className="lab-profile-pw-field">
              <label htmlFor="lab-pw-new">Neues Passwort</label>
              <input
                type="password"
                id="lab-pw-new"
                value={pwNew}
                onChange={e => setPwNew(e.target.value)}
                required
                minLength={8}
                placeholder="Min. 8 Zeichen, 1 Grossbuchstabe, 1 Sonderzeichen"
              />
            </div>
            <div className="lab-profile-pw-field">
              <label htmlFor="lab-pw-confirm">Neues Passwort bestätigen</label>
              <input
                type="password"
                id="lab-pw-confirm"
                value={pwConfirm}
                onChange={e => setPwConfirm(e.target.value)}
                required
                minLength={8}
                placeholder="Passwort wiederholen"
              />
            </div>
            <button type="submit" className="lab-profile-pw-btn" disabled={pwLoading}>
              {pwLoading ? 'Wird geändert...' : 'Passwort ändern'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
